import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Storage key
const STORAGE_KEY = 'elevana_signup_form';

// Initial state
export const initialFormData = {
    // Step tracking
    currentStep: 'eligibility',

    // Property / Target
    TargetAddressOne: '',
    TargetAddressTwo: '',
    TargetCity: '',
    TargetState: '',
    TargetZipCode: '',
    TargetLat: null,
    TargetLng: null,
    TargetHomeStatus: '',          // primaryResidence | vacationHome | rentalProperty
    TargetHomeStatusOther: '',
    TargetAddressFormatted: '',

    // Property checklist (what's exposed)
    ExposedHome: false,
    ExposedLandImprovements: false,
    ExposedHardscaping: false,
    ExposedContents: false,
    ExposedValuables: false,
    ExposedVehicles: false,
    ExposedLivingExpenses: false,

    // Eligibility
    IsExposedValueOverMillion: null,   // true | false | null (unanswered)

    // Contact / Insured
    FirstName: '',
    LastName: '',
    UserName: '',                      // email
    PhoneNumberPrimary: '',
    PhoneNumberSecondary: '',
    IsReceivingCriticalMessages: false,
    OwnerType: 'INDIVIDUAL',           // maps to entityType

    // Coverage
    CoverageAmount: 20000,

    // Data point selection (from API rate_options)
    SelectedTriggerType: '',           // single | dual | triple
    SelectedTotalPremium: 0,
    QuoteNumber: '',
    RateOptions: [],                   // raw from API

    // Mailing / Contact address
    ContactAddressOne: '',
    ContactAddressTwo: '',
    ContactCity: '',
    ContactState: '',
    ContactZipCode: '',
    ContactCountry: 'USA',
    ContactHomeStatus: '',
    MailingSameAsProperty: true,

    // Additional parties
    HasAdditionalParties: false,
    AdditionalPartyType: '',
    AdditionalPartyFirstName: '',
    AdditionalPartyLastName: '',
    AdditionalPartyName: '',
    AdditionalPartyAddressOne: '',
    AdditionalPartyAddressTwo: '',
    AdditionalPartyCity: '',
    AdditionalPartyState: '',
    AdditionalPartyZipCode: '',
    AdditionalPartyCountry: 'USA',

    // Payment
    PaymentType: '', // ach | creditCard

    // NM issuePolicy return values
    PolicyNumber: '',
    InvoiceNumber: '',
    ExpiryDate: '',
    EffectiveDate: '',
    PolicyTotalPremium: null,
    Status: '', // '' | Active | Referred

    // Database record tracking
    // QuoteId is null until SaveQuote succeeds
    // used to determine whether to call SaveQuote (first time) or UpdateQuote (subsequent steps)
    QuoteId: null,

    // DocuSign 
    EnvelopeId: '',

    // Legacy / misc
    TempId: null,
    IsOwnerOfProperty: false,
    PaymentFrequency: '',
};

// Reducer
function formReducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_FIELDS':
            return { ...state, ...action.payload };
        case 'SET_STEP':
            return { ...state, currentStep: action.step };
        case 'RESET':
            return { ...initialFormData };
        default:
            return state;
    }
}

// Context
const FormContext = createContext(null);

// Load from localStorage
function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

// Provider
export function FormProvider({ children }) {
    const saved = loadFromStorage();
    const [formData, dispatch] = useReducer(
        formReducer,
        saved ? { ...initialFormData, ...saved } : initialFormData
    );

    // Sync to localStorage on every change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
        } catch {
            // storage full or unavailable — fail silently
        }
    }, [formData]);

    const setField = (field, value) =>
        dispatch({ type: 'SET_FIELD', field, value });

    const setFields = (payload) =>
        dispatch({ type: 'SET_FIELDS', payload });

    const setStep = (step) =>
        dispatch({ type: 'SET_STEP', step });

    const resetForm = () => {
        localStorage.removeItem(STORAGE_KEY);
        dispatch({ type: 'RESET' });
    };

    return (
        <FormContext.Provider value={{ formData, setField, setFields, setStep, resetForm }}>
            {children}
        </FormContext.Provider>
    );
}

// Hook
export function useFormContext() {
    const ctx = useContext(FormContext);
    if (!ctx) throw new Error('useFormContext must be used inside <FormProvider>');
    return ctx;
}